import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { Ctx } from 'components/CtxFormFields/CtxInputs';
import RenderFormItem from 'components/CtxFormFields/FormFieldCtx';
import { getMSDataByEntity } from 'rdx/hardware/installer';
import { addSuccess, addWarning } from 'rdx/alert';
import { getIntegrationConfig } from 'service/InstallerApp';
import { updateMSConfiguration } from 'service/InstallerApp/requesthandlers';
import useErrorState, { translateError } from 'utils/hooks/useErrorState';
import { AutoInstallInputField } from 'pages/Hardware/Integrations/IntegrationForm';

import goMSFields from './formFields';

function GoMSForm({ resetConfigState, saveConfigToCAFA }) {
  const [swedbankInfo, setSwedbankInfo] = useState({});
  const dispatch = useDispatch();
  const { values, onChange: contextOnChange } = useContext(Ctx);
  const { msIsInstalled, entity, version } = useSelector(
    getMSDataByEntity('printing-microservice'),
  );

  const [nameOptions, setNameOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const options = {
    PrinterName: nameOptions,
    PrinterVendor: vendorOptions,
  };
  const [printers, setPrinters] = useState({});
  const [msConfig, setMsConfig] = useState({});
  const combinedValues = useMemo(
    () => ({ ...msConfig, ...values?.goMS }),
    [msConfig, values?.goMS],
  );

  const { errors, hasErrors } = useErrorState(combinedValues, goMSFields);

  const getPrintersEndpoint = `https://localhost.erply.com:${
    combinedValues.Port ?? 5000
  }/getprinterlist?full=${!!combinedValues.PrinterGetFullList}`;

  const getVendorsEndpoint = `https://localhost.erply.com:${
    combinedValues.Port ?? 5000
  }/getvendorlist`;

  const {
    entity: swedEntity,
    installedVersion: swedInstalledVersion,
    msIsInstalled: swedMsIsInstalled,
  } = useSelector(getMSDataByEntity('swedbank-ingenico'));

  useEffect(() => {
    if (swedEntity && swedInstalledVersion) {
      getIntegrationConfig(swedEntity, swedInstalledVersion).then(
        ({ data }) => {
          try {
            const config = JSON.parse(data.config);
            setSwedbankInfo(config);
          } catch (err) {
            console.error(err);
          }
        },
      );
    }
  }, [swedEntity, swedInstalledVersion, swedMsIsInstalled]);
  useEffect(() => {
    if (Object.keys(swedbankInfo).length) {
      contextOnChange('goMS', {
        PrinterTerminalPort: swedbankInfo.Port,
      });
    }
  }, [swedbankInfo]);

  useEffect(() => {
    axios
      .get(getPrintersEndpoint)
      .then(({ data }) => {
        const prNames = {};
        data.forEach((d) => {
          prNames[d.Name] = d;
        });
        setPrinters(prNames);
        setNameOptions(data.map((d) => d.Name));
      })
      .catch((err) => {
        console.error(err);
        dispatch(
          addWarning(
            'Unable to fetch printer options from printing-microservice',
          ),
        );
        setNameOptions([{ name: 'No printers', value: '' }]);
      });
  }, [getPrintersEndpoint]);

  useEffect(() => {
    axios
      .get(getVendorsEndpoint)
      .then(({ data }) => setVendorOptions(data))
      .catch((err) => {
        console.error(err);
        dispatch(
          addWarning(
            'Unable to fetch vendor options from printing-microservice',
          ),
        );
        setVendorOptions(['No vendor']);
      });
  }, [getVendorsEndpoint]);

  // if there is no preexisting configuration in CAFA, update the satte values with the microservice config
  useEffect(() => {
    if (
      Object.keys(msConfig).length &&
      !Object.keys(values?.goMS ?? {}).length
    ) {
      const payload = {};
      Object.keys(goMSFields).forEach((key) => {
        payload[key] = msConfig[key];
      });
      contextOnChange('goMS', payload);
    }
  }, [msConfig, values]);

  // get options from printing ms
  useEffect(() => {
    if (msIsInstalled) {
      getIntegrationConfig(entity, version)
        .then(({ data }) => {
          try {
            const config = JSON.parse(data.config);
            setMsConfig(config);
          } catch (e) {
            console.error(e);
          }
        })
        .catch(console.error);
    }
  }, [msIsInstalled]);

  const handleChange = (key, value) => {
    switch (key) {
      case 'PrinterName':
        contextOnChange('goMS', {
          [key]: value,
          PrinterVendor: printers[value]?.VendorID ?? '',
        });
        break;
      case 'PrinterPaperSize':
      case 'PrinterCharactersPerLine':
        contextOnChange('goMS', { [key]: Number(value.replace(/\D/g, '')) });
        break;
      case 'PrinterFullCut':
      case 'PrinterOpenCashDrawer':
      case 'PrinterGetFullList':
        contextOnChange('goMS', { [key]: Boolean(value) });
        break;
      default:
        contextOnChange('goMS', { [key]: value });
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasErrors) {
      return dispatch(addWarning('Printer name is required.'));
    }
    const { enabled, ...config } = values?.goMS ?? {};
    await saveConfigToCAFA('goMS');
    const payload = {};
    // transforms 0/1 used for Boolean to Boolean values
    Object.entries(msConfig).forEach(([key, value]) => {
      if (config[key]) {
        payload[key] =
          typeof value === 'boolean' && typeof config[key] === 'number'
            ? Boolean(config[key])
            : config[key];
      } else {
        payload[key] = value;
      }
    });
    await updateMSConfiguration(entity, version, config).catch(console.error);
    dispatch(addSuccess('Configuration saved'));
    return true;
  };

  const value = useMemo(
    () => ({ values: combinedValues, onChange: handleChange }),
    [combinedValues, handleChange],
  );

  if (!msIsInstalled) return null;

  return (
    <Ctx.Provider value={value}>
      <Form onSubmit={handleSubmit}>
        {entity && (
          <AutoInstallInputField
            entity={entity}
            value={values?.goMS?.version}
            onChange={(v) =>
              contextOnChange('goMS', {
                version: v,
              })
            }
          />
        )}
        {Object.values(goMSFields).map(({ validate, ...field }) => (
          <RenderFormItem
            disabled={field.id === 'PrinterTerminalPort'}
            key={field.id}
            size="md"
            name={field.id}
            error={translateError(errors[field.id])}
            {...field}
            options={
              options[field.id]
                ? [{ value: '', name: 'Select printer' }, ...options[field.id]]
                : field.options
            }
          />
        ))}
        <Button type="button" onClick={() => resetConfigState('goMS')}>
          Reset
        </Button>
        <Button
          className="ml-1"
          disabled={hasErrors}
          variant="success"
          type="submit"
        >
          Save
        </Button>
      </Form>
    </Ctx.Provider>
  );
}

export default GoMSForm;
